<template>
  <div class="wrapper">
    <h3 v-html="title"></h3>
    <div v-html="content"></div>

    <div class="file-input-container">
      <div class="file-input-wrapper">
        <div class="input-label">Choose your video</div>
        <input
          type="file"
          name="videoInput"
          id="videoInput"
          :size="maxSize"
          :accept="extensions.join(',')"
          ref="videoInput"
          @change="filesChange($event.target.files)"
        />

        <label class="file-label" for="logoInput">
          <span v-if="isClean">
            <i class="fas fa-file"></i> Choose a file... (Max
            {{ maxFileSize }}Mb)
          </span>
          <span v-if="!isClean">{{ fileName }} selected... </span>
          <span class="alert-box" v-if="hasError">
            {{ error }}
          </span>
        </label>
      </div>
      <div class="preview-container" v-if="source">
        <div class="vid-delete" title="Delete video">
          <button type="button" v-on:click="clearVideo">
            <i class="fas fa-times fw"></i>
          </button>
        </div>
        <video :key="fileKey" controls>
          <source :src="source" :type="sourceType" id="videoPreview" />
          Your browser does not support HTML5 video.
        </video>
      </div>
    </div>

    <div class="success-message" v-if="isSuccessful">
      Success! Your video has been submitted!
    </div>

    <div class="submit-container">
      <button
        type="button"
        v-on:click="uploadVideo"
        :disabled="isClean || hasError || isUploading"
      >
        <span v-if="!isUploading">Send Your Video</span>
        <span v-if="isUploading">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "edo-video-upload",

  data: function () {
    return {
      error: null,
      extensions: [".mp4", ".mkv"],
      fileName: "",
      source: null,
      sourceType: null,
      file: null,
      fileContent: null,
      fileKey: null,
      uploading: false,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      isSuccessful: false,
    };
  },

  props: {
    title: {
      type: String,
      default: "We need your creativity!",
    },
    content: {
      type: String,
      default:
        '<p>&nbsp;</p><p class="text-center">Help us make a bigger impact by creating a #DefendTheUnburnt video that our politicians can&rsquo;t ignore! <br />Create a short video of yourself explaining why you&rsquo;re raising your voice for our unburnt forests. </p><p class="text-center">It could be through a speech, a painting, photography, a hike in nature&hellip; anything that is meaningful to you!</p>',
    },
    maxFileSize: {
      type: Number,
      default: 80,
    },
  },

  computed: {
    isClean() {
      return this.file === null;
    },
    hasError() {
      return this.error != null && this.error.length > 0;
    },
    isUploading() {
      return this.uploading;
    },
    maxSize() {
      return this.maxFileSize * 1000000;
    },
    ...mapState({
      product: (state) => state.product.product,
      lead: (state) => state.campaign.edo,
      leadFirstName: (state) => state.campaign.edo.firstName,
      leadLastName: (state) => state.campaign.edo.lastName,
      leadEmail: (state) => state.campaign.edo.email,
      leadPhoneNumber: (state) => state.campaign.edo.phoneNumber,
    }),
  },

  watch: {
    leadFirstName(newVal, oldVal) {
      if (newVal && newVal.length > 0) {
        this.firstName = newVal;
      }
    },
    leadLastName(newVal, oldVal) {
      if (newVal && newVal.length > 0) {
        this.lastName = newVal;
      }
    },
    leadEmail(newVal, oldVal) {
      if (newVal && newVal.length > 0) {
        this.email = newVal;
      }
    },
    leadPhoneNumber(newVal, oldVal) {
      if (newVal && newVal.length > 0) {
        this.phoneNumber = newVal;
      }
    },
  },

  created: function () {
    this.firstName = this.leadFirstName;
    this.lastName = this.leadLastName;
    this.email = this.leadEmail;
    this.phoneNumber = this.leadPhoneNumber;
  },

  methods: {
    filesChange(fileList) {
      try {
        if (!fileList.length) return;
        const file = fileList[0];

        if (file.size <= this.maxSize) {
          // Check extension of the file
          const dotIndex = file.name.lastIndexOf(".");

          if (dotIndex < 0) {
            this.error = "File name is missing extension";
            return;
          }

          const extension = file.name.substring(dotIndex).toLowerCase();
          if (!this.extensions.includes(extension)) {
            this.error = `Please use only these formats: ${this.extensions.join(
              ", "
            )}`;
            return;
          }

          this.file = file;
          this.fileName = file.name;
          this.error = null;

          // This key will force Vue.js to update the video component
          // Otherwise, only the source is changed and it doesn't trigger
          // The update of the video component
          this.fileKey = new Date().getTime();

          // Allow to preview
          this.sourceType = file.type;
          this.source = URL.createObjectURL(file);
        } else {
          this.error = "File too big!";
        }
      } catch (err) {
        this.error = "Cannot read the file, please try another one.";
      }
    },
    clearVideo() {
      this.$refs.videoInput.value = "";
      this.fileName = "";
      this.source = null;
      this.sourceType = null;
      this.file = null;
      this.fileKey = null;
    },
    async uploadVideo() {
      try {
        console.log("Upload");
        if (this.file) {
          this.uploading = true;

          // Upload the logo
          const formData = new FormData();
          formData.append("edo-video", this.file, this.fileName);
          const url = await this.$store.dispatch("uploadEDOVideo", formData);

          // Save the meta data to be reviewed
          const meta = {
            campaignId: "edo",
            uploadDate: new Date().toISOString(),
            email: this.email,
            phoneNumber: this.phoneNumber,
            firstName: this.firstName,
            lastName: this.lastName,
            url,
            mimeType: this.sourceType,
            fileName: this.fileName,
          };

          await this.$store.dispatch("createFile", meta);

          // Clear the input
          this.clearVideo();

          this.isSuccessful = true;
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.uploading = false;

        setTimeout(() => {
          this.isSuccessful = false;
        }, 20000);
      }
    },
  },
};
</script>

<style scoped>
.wrapper {
  border: 3px solid var(--wwfRegenerateAustralia);
  padding: 10px;
}

.wrapper h3 {
  margin: auto;
  text-align: center;
  width: fit-content;
}
.wrapper h3::before {
  left: 50%;
  transform: translateX(-50%);
}

.file-input-container {
  display: block;
  margin: 20px auto;
  min-width: 50%;
  max-width: 75%;
}

.file-input-wrapper {
  position: relative;
}

.input-label {
  font-weight: 600;
}

input[type="file"] {
  opacity: 0;
  z-index: 15;
  position: absolute;
  left: 0;
  bottom: 0;
  top: 0;
  margin-bottom: 0;
  cursor: pointer;
}

.file-label {
  margin-top: 8px;
  padding: 12px;
  border: 3px solid var(--wwfOrange);
  border-radius: 8px;
  cursor: pointer;
}

.file-label i {
  margin-right: 6px;
}
.preview-container {
  position: relative;
  width: 80%;
  max-height: 300px;
  margin-top: 8px;
  margin-left: auto;
  margin-right: auto;
}

.preview-container .vid-delete {
  z-index: 100;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 4px;
  right: 4px;
  border-radius: 50%;
  background-color: var(--wwfWhite);
  width: 20px;
  height: 20px;
  font-size: 14px;
  cursor: pointer;
}

.preview-container .vid-delete button {
  padding: 0;
  color: var(--wwfBlack);
  background-color: transparent;
  cursor: pointer;
}

.preview-container .vid-delete i {
  margin: 0;
  cursor: pointer;
}

.preview-container video {
  display: block;
  margin: auto;
  /* width: 100%; */
  max-height: 300px;
}

.submit-container {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 16px;
}

.submit-container button {
  padding: 18px;
  color: var(--wwfWhite);
  font-weight: 600;
  background-color: var(--wwfBlue);
  cursor: pointer;
}

.submit-container button:disabled {
  color: var(--wwfGreyA7);
  background-color: var(--wwfGreyDE);
  cursor: default;
}

@media only screen and (max-width: 64.063em) {
  video {
    display: inline-block;
  }
}

.success-message {
  text-align: center;
  color: var(--wwfRegenerateFontColor);
  font-size: 18px;
  font-weight: 600;
}
</style>


// WEBPACK FOOTER //
// EdoVideoUpload.vue?80a2808e